import type { HealthDto } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { api, type ApiError } from '../../api.ts';
import { useFeedbackRecord } from '../../components/feedback-context.ts';
import { Loading, Page, Section, TextLink, Title } from '../../components/ui.tsx';
import { currentEnvironment } from '../../environment.ts';
import { isAdmin, isStaff, useMe } from '../../session.tsx';
import { BundlesSection } from './Bundles.tsx';
import { KeysSection } from './Keys.tsx';
import { PatternPicturesSection } from './PatternPictures.tsx';
import { TestProjectsSection } from './TestProjects.tsx';

const ENV_WORDS: Record<HealthDto['env'], string> = {
  production: 'Production',
  development: 'Development, on this machine',
  test: 'Test',
};

/** A build in words: the commit's first seven characters and its branch, or that it is not a release build at all. */
const buildWords = (sha: string, ref?: string) => (sha === 'dev' ? 'Not a release build (dev)' : `${sha.slice(0, 7)}${ref && ref !== 'dev' ? ` · ${ref}` : ''}`);

/**
 * Settings → Development tools: what is answering (the API's environment and
 * build, and this web app's), the reports filed in the portal (Bugs and
 * requests, for admins) and what each release said (the development blog), a
 * key for Claude to reach the process tree's draft, the pattern pictures and
 * painting those still without one (PatternPictures.tsx), copying a project built here to
 * production (bundles), and, in development on this machine, resetting the test
 * projects to the documented jobs (TestProjects.tsx).
 */
export function DevelopmentSettings() {
  const { data: me } = useMe();
  const admin = isAdmin(me);
  const { data: health, error } = useQuery<HealthDto, ApiError>({ queryKey: ['health'], queryFn: () => api<HealthDto>('/health'), staleTime: 60_000 });
  useFeedbackRecord(null, null);
  const data = currentEnvironment();
  const rows: [string, string][] = health
    ? [
        ['API', ENV_WORDS[health.env] ?? health.env],
        ['API build', buildWords(health.build.sha, health.build.ref)],
        ['Web app build', buildWords(import.meta.env.VITE_COMMIT_SHA ?? 'dev')],
        ...(data ? ([['Data', data === 'production' ? 'Production, through the production API' : 'Local, this machine’s database']] as [string, string][]) : []),
      ]
    : [];
  return (
    <Page>
      <Title kicker="Settings">Development tools</Title>
      <p className="max-w-measure text-ink-2">What the portal is running, where reports about it go, and what each release changed.</p>
      <Section title="What is answering">
        {error ? <p className="text-alert">The API did not answer: {error.message}</p> : !health ? <Loading /> : null}
        {rows.length ? (
          <dl className="divide-y divide-rule border-b border-rule">
            {rows.map(([k, v]) => (
              <div key={k} className="grid gap-x-6 py-3 sm:grid-cols-[9rem_1fr]">
                <dt className="label pt-0.5">{k}</dt>
                <dd className="num text-[0.9375rem]">{v}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </Section>
      {admin ? <TestProjectsSection /> : null}
      <Section title="Reports and releases">
        <ul className="divide-y divide-rule border-b border-rule">
          {admin ? (
            <li className="py-3">
              <TextLink to="/settings/reports">Bugs and feature requests</TextLink>
              <p className="mt-0.5 text-sm text-ink-2">Every bug, feature request and comment filed in the portal, with its GitHub issue.</p>
            </li>
          ) : null}
          <li className="py-3">
            <TextLink to="/settings/blog">The development blog</TextLink>
            <p className="mt-0.5 text-sm text-ink-2">Each release in plain words: what changed, what was added, what was fixed. Public too, linked from the sign-in page.</p>
          </li>
        </ul>
      </Section>
      {admin ? <KeysSection /> : null}
      {isStaff(me) ? <PatternPicturesSection editable={admin} /> : null}
      {admin ? <BundlesSection /> : null}
    </Page>
  );
}
